import axios, { type InternalAxiosRequestConfig } from 'axios'
import { refreshAccessToken } from './refresh'

// API Base URL from environment with production fallback
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? '/api/v1' : 'http://localhost:8880/api/v1')

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-Source': 'workspace',
  },
})

// Internal flag on a request config to prevent an infinite 401 → refresh → 401 loop.
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor: on 401 attempt one refresh + replay before giving up.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const original = error.config as RetriableConfig | undefined

    if (status !== 401 || !original) {
      return Promise.reject(error)
    }

    const url = original.url || ''
    console.warn(`[Auth] 401 on ${original.method?.toUpperCase()} ${url}`)

    // Never try to refresh the refresh endpoint itself.
    if (url.includes('/token/refresh')) {
      console.error('[Auth] refresh endpoint itself returned 401 — session dead')
      handleAuthFailure()
      return Promise.reject(error)
    }

    // Only attempt one refresh-and-retry per original request.
    if (original._retry) {
      console.error('[Auth] retry after refresh still got 401 — session dead')
      handleAuthFailure()
      return Promise.reject(error)
    }
    original._retry = true

    console.log('[Auth] attempting token refresh before retry…')
    const newToken = await refreshAccessToken()
    if (!newToken) {
      console.error('[Auth] token refresh failed — session dead')
      handleAuthFailure()
      return Promise.reject(error)
    }

    console.log('[Auth] token refreshed, replaying request')
    original.headers.Authorization = `Bearer ${newToken}`
    return apiClient.request(original)
  },
)

/**
 * Called when we are certain the user's session is dead (refresh token is
 * gone, invalid, or expired). Clears storage and notifies the global listener
 * mounted in AuthProvider, which handles toast + redirect.
 */
function handleAuthFailure() {
  const hadToken = !!localStorage.getItem('auth_token')
  localStorage.removeItem('auth_token')
  localStorage.removeItem('refresh_token')
  if (hadToken) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: 401 } }))
  }
}

// LCA API - Core business functionality
export const lcaAPI = {
  searchActivities: (params: { q: string; database: string; location?: string; limit?: number }) =>
    apiClient.get('/activities/search', { params }),

  searchBiosphere: (params: {
    q: string
    database: string
    compartment?: string
    sub_compartment?: string
    flow_type?: string
    unit?: string
    limit?: number
  }) => apiClient.get('/biosphere/flows/search', { params }),

  calculateStandard: (params: {
    activity_code: string
    database: string
    quantity: number
    method_specification: string
  }) => apiClient.post('/lca/calculate/standard', params),

  calculateCustom: (params: {
    project_name: string
    activity_code: string
    quantity: number
    method_specification: string
  }) => apiClient.post('/lca/calculate/custom', params),

  calculateHotspotsCustom: (params: {
    project_name: string
    activity_code: string
    method_specification: string
    top_n?: number
  }) => apiClient.post('/lca/calculate/hotspots/custom', params),

  getTaskStatus: (taskId: string) => apiClient.get(`/calculations/results/${taskId}`),

  getCalculationHistory: (params?: { limit?: number; offset?: number }) =>
    apiClient.get('/calculations/history', { params }),

  searchMethods: (params: {
    keywords: string
    database: string
    category?: string
    limit?: number
  }) => apiClient.get('/impact-methods/search', { params }),

  getMethodCategories: (database: string) =>
    apiClient.get('/impact-methods/categories', { params: { database } }),

  getActivityDetails: (
    activityCode: string,
    databaseName: string,
    includeExchanges: boolean = false,
  ) =>
    apiClient.get(`/activity/details/${activityCode}`, {
      params: { database_name: databaseName, include_exchanges: includeExchanges },
    }),

  getProjects: (params?: { page?: number; limit?: number }) =>
    apiClient.get('/projects', { params }),

  createProject: (params: {
    project_name: string
    description: string
    database: string
    notes?: string
  }) => apiClient.post('/projects', params),

  exportProject: (projectId: string, format: string = 'ecospold1') =>
    apiClient.get(`/projects/${projectId}/export`, {
      params: { format },
    }),

  deleteProject: (projectId: string) => apiClient.delete(`/projects/${projectId}`),
}

// User API - Account and subscription management
export const userAPI = {
  // Enhanced user info with tier, tools, databases
  getUserWithTier: () => apiClient.get('/user/me/'),

  // API activity for MCP dashboard
  getApiActivity: (params?: { limit?: number; hours?: number }) =>
    apiClient.get('/user/me/tool-activity', { params }),

  // Usage data
  getUsage: () => apiClient.get('/call_packs/usage'),

  getDetailedUsage: () => apiClient.get('/call_packs/detailed-usage'),

  getDashboardBalances: () => apiClient.get('/dashboard/balances'),

  // Call pack management
  getCallPackHistory: (params?: { page?: number; items_per_page?: number }) =>
    apiClient.get('/call_packs/history', { params }),

  getCallPackPricing: () => apiClient.get('/call_packs/pricing'),

  getSubscription: () => apiClient.get('/subscriptions/'),

  // API tokens
  getApiTokens: () => apiClient.get('/api_tokens'),

  getOAuthTokens: () => apiClient.get('/api_tokens/oauth'),

  createApiToken: (name: string) => apiClient.post('/api_tokens', { name }),

  deleteApiToken: (tokenId: number) => apiClient.delete(`/api_tokens/${tokenId}`),

  // MCP Configuration
  getMcpConfig: () => apiClient.get('/mcp/config'),

  updateProfile: (data: Record<string, unknown>) => apiClient.put('/users/me', data),

  // Chat usage tracking
  getChatUsage: () => apiClient.get('/chat/usage'),

  // Account deletion
  deleteAccount: () => apiClient.delete('/users/me/account'),

  // Export user data (GDPR compliance)
  exportUserData: () => apiClient.get('/users/me/export-data'),

  // Feature flags
  getFeatures: () => apiClient.get('/features'),

  checkFeature: (featureName: string) => apiClient.get(`/features/${featureName}`),

  // Spectator / Live Share
  createSpectatorLink: (durationHours: number = 4, includeExisting: boolean = true) =>
    apiClient.post('/workspace/spectator', {
      duration_hours: durationHours,
      include_existing: includeExisting,
    }),

  revokeSpectatorLink: (shareId: string) => apiClient.delete(`/workspace/spectator/${shareId}`),

  validateSpectatorLink: (shareId: string) =>
    apiClient.get(`/workspace/spectator/${shareId}/validate`),

  setSpectatorAlias: (alias: string, shareId: string) =>
    apiClient.put(`/workspace/spectator/alias/${alias}`, { share_id: shareId }),

  deleteSpectatorAlias: (alias: string) => apiClient.delete(`/workspace/spectator/alias/${alias}`),

  getAliasForShare: (shareId: string) => apiClient.get(`/workspace/spectator/${shareId}/alias`),

  getActiveSpectatorSession: () => apiClient.get('/workspace/spectator/active'),

  getMyLiveLinks: () => apiClient.get('/workspace/spectator/my-links'),

  toggleLiveLinkVisibility: (shareId: string) =>
    apiClient.patch(`/workspace/spectator/${shareId}/visibility`),

  createLiveLink: (projectId: string) => apiClient.post(`/workspace/live-link/${projectId}`),

  // Playground MCP servers
  getPlaygroundMcpServers: () => apiClient.get('/chat/mcp-servers'),

  togglePlaygroundMcpServer: (serverUuid: string, enabled: boolean) =>
    apiClient.post(`/chat/mcp-servers/${serverUuid}/toggle`, null, {
      params: { enabled },
    }),

  // Custom system prompt
  getCustomSystemPrompt: () => apiClient.get('/chat/system-prompt'),

  setCustomSystemPrompt: (customSystemPrompt: string | null) =>
    apiClient.put('/chat/system-prompt', null, {
      params: { custom_system_prompt: customSystemPrompt ?? '' },
    }),
}

// Subscription API - Paddle integration
export const subscriptionAPI = {
  getStatus: () => apiClient.get('/paddle/user-subscription-status'),

  createCheckout: (priceId: string) =>
    apiClient.post('/paddle/create-checkout', {
      items: [{ price_id: priceId, quantity: 1 }],
      success_url: `${window.location.origin}/checkout/success`,
      cancel_url: `${window.location.origin}/pricing`,
    }),

  createCheckoutPublic: (priceId: string) =>
    apiClient.post('/paddle/create-checkout-public', {
      items: [{ price_id: priceId, quantity: 1 }],
      success_url: `${window.location.origin}/checkout/success`,
      cancel_url: `${window.location.origin}/#pricing`,
    }),

  // Call pack checkout - uses internal price ID (integer)
  createCallPackCheckout: (priceId: number, packSize: number) =>
    apiClient.post('/paddle/create-checkout', {
      items: [{ price_id: priceId, quantity: 1 }],
      success_url: `${window.location.origin}/checkout/success?type=callpack&pack_size=${packSize}`,
      cancel_url: `${window.location.origin}/dashboard?tab=usage`,
    }),

  cancelSubscription: (effective_from = 'next_billing_period') =>
    apiClient.post('/paddle/subscription/cancel', { effective_from }),

  upgradeSubscription: (new_price_id: string) =>
    apiClient.post('/paddle/subscription/upgrade', { new_price_id }),

  createPortalSession: () => apiClient.post('/paddle/customer-portal-session'),

  getProducts: () => apiClient.get('/paddle/products'),

  getSubscriptionProducts: () =>
    apiClient.get('/products/', { params: { product_type: 'subscription' } }),

  getTiers: () => apiClient.get('/tiers'),

  getTiersWithTools: () => apiClient.get('/tiers/with-tools'),

  // Subscription change management
  previewSubscriptionChange: (userUuid: string, targetTierId: number) =>
    apiClient.post(`/subscription_changes/${userUuid}/preview-upgrade`, {
      target_tier_id: targetTierId,
    }),

  requestSubscriptionChange: (userUuid: string, targetTierId: number) =>
    apiClient.post(`/subscription_changes/${userUuid}/change-tier`, {
      target_tier_id: targetTierId,
    }),

  getSubscriptionChangeStatus: (userUuid: string) =>
    apiClient.get(`/subscription_changes/${userUuid}/status`),

  getAvailableTierChanges: (userUuid: string) =>
    apiClient.get(`/subscription_changes/${userUuid}/available-changes`),

  cancelPendingDowngrade: (userUuid: string) =>
    apiClient.delete(`/subscription_changes/${userUuid}/pending-change`),
}

export default apiClient
